import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Download } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, InfoRow } from "@/components/shared";

export const ResetDataCard = () => {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/settings/reset-summary").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  const backup = async () => { const r = await api.get("/settings/export-backup", { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `marksman-backup-${new Date().toISOString().slice(0, 10)}.json`; a.click(); };
  const reset = async () => {
    setBusy(true);
    try { const { data } = await api.post("/settings/reset-all", { confirmation: text }); toast.success(`Business data reset — ${Object.values(data.removed).reduce((a, b) => a + b, 0)} records removed`); setOpen(false); setText(""); load(); setTimeout(() => window.location.reload(), 800); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!s) return null;
  const total = s.counts.reduce((a, c) => a + c.count, 0);
  return (
    <Card title={<span className="flex items-center gap-2 text-rose-800"><AlertTriangle className="h-4 w-4" /> Reset all business data</span>} testid="reset-data-card" className="max-w-xl border-rose-200">
      <p className="text-sm text-slate-600 leading-relaxed">Removes <strong>every</strong> business record — sample or manually entered — and their uploaded files. Login accounts, roles, currencies, statuses and the approved default project types and departments are kept. Demonstration data is not re-created afterwards.</p>
      <div className="mt-3 space-y-0" data-testid="reset-summary">
        {s.counts.map((c) => <InfoRow key={c.key} label={c.label} testid={`reset-count-${c.key}`}><span className="tabular-nums font-medium">{c.count}</span></InfoRow>)}
        <InfoRow label="Uploaded files to remove">{s.files}</InfoRow>
        <InfoRow label="Custom project types / departments">{s.custom_project_types.length + s.custom_departments.length ? [...s.custom_project_types, ...s.custom_departments].join(", ") : "none"}</InfoRow>
        <InfoRow label="Login accounts (kept)">{s.users_kept}</InfoRow>
        {s.last_reset && <InfoRow label="Last reset" testid="last-reset">{fmtDateTime(s.last_reset.at)} by {s.last_reset.by}</InfoRow>}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" onClick={backup} data-testid="export-backup-button"><Download className="h-4 w-4 mr-1" /> Download backup (JSON)</Button>
        {!open && <Button variant="destructive" onClick={() => setOpen(true)} disabled={total === 0} data-testid="reset-open-button">Reset all business data…</Button>}
      </div>
      {open && (
        <div className="mt-4 rounded-md border border-rose-300 bg-rose-50 p-4 space-y-3" data-testid="reset-confirm-box">
          <div className="text-sm text-rose-900 font-medium">This permanently deletes {total} records and {s.files} files. Download a backup first if you may need them.</div>
          <label className="block text-xs text-rose-900">Type <span className="font-mono font-semibold">RESET ALL DATA</span> to confirm
            <Input data-testid="reset-confirm-input" value={text} onChange={(e) => setText(e.target.value)} className="mt-1 bg-white" autoFocus /></label>
          <div className="flex gap-2">
            <Button variant="destructive" disabled={busy || text !== "RESET ALL DATA"} onClick={reset} data-testid="reset-execute-button">{busy ? "Resetting…" : "Permanently reset"}</Button>
            <Button variant="outline" onClick={() => { setOpen(false); setText(""); }} data-testid="reset-cancel-button">Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
};
